import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GroupOption } from "../types";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/api";
import { LocalStorageManager } from "../services/localStorage";
import "./group-selection-page.css";

const GroupSelectionPage: React.FC = () => {
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [selectedGroupSlug, setSelectedGroupSlug] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>("");

  const { user, switchGroup, logout } = useAuth();
  const navigate = useNavigate();

  // Load user's groups on component mount
  useEffect(() => {
    const loadUserGroups = async () => {
      try {
        setIsLoading(true);
        const response = await apiService.getUserGroups();

        // Handle new API response format
        const groupsData = response.groups || response;

        // Transform backend response to GroupOption format
        const groupOptions: GroupOption[] = groupsData.map((group: any) => ({
          id: group.id,
          slug: group.slug,
          name: group.name,
          isAdmin: group.isAdmin || false,
          memberCount: group.memberCount || 0,
        }));
        setGroups(groupOptions);

        // If user has no groups, redirect to group creation
        if (groupOptions.length === 0) {
          navigate("/create-group");
          return;
        }

        // If user has only one group, redirect directly to dashboard
        if (groupOptions.length === 1) {
          const singleGroup = groupOptions[0];
          navigate(`/${singleGroup.slug}/dashboard`);
          return;
        }

        // Pre-select last used group if available
        if (user) {
          const lastSelectedGroup = LocalStorageManager.getLastSelectedGroup(
            user.id
          );
          if (lastSelectedGroup) {
            const availableGroupSlugs = groupOptions.map((g) => g.slug);
            if (
              LocalStorageManager.isValidGroupForUser(
                user.id,
                lastSelectedGroup,
                availableGroupSlugs
              )
            ) {
              setSelectedGroupSlug(lastSelectedGroup);
            }
          }
        }
      } catch (err: any) {
        console.error("Failed to load user groups:", err);
        setError("Errore nel caricamento dei gruppi. Riprova più tardi.");
      } finally {
        setIsLoading(false);
      }
    };

    // Load groups even if user is not fully authenticated yet (for temp session)
    loadUserGroups();
  }, [user, navigate]);

  const handleGroupSelect = (groupSlug: string) => {
    setSelectedGroupSlug(groupSlug);
  };

  const handleSubmit = async () => {
    if (!selectedGroupSlug) return;

    try {
      setIsSubmitting(true);
      setError("");

      // Call backend to select group and create full session
      const response = await apiService.api.post("/auth/select-group", {
        groupSlug: selectedGroupSlug,
      });

      if (response.data.success) {
        // Update application state with selected group
        if (user) {
          await switchGroup(selectedGroupSlug);
          // Save the selected group to localStorage
          LocalStorageManager.setLastSelectedGroup(user.id, selectedGroupSlug);
        }

        // Navigate to the selected group's dashboard using window.location for full page reload
        window.location.href = `/${selectedGroupSlug}/dashboard`;
      } else {
        setError("Errore nella selezione del gruppo. Riprova.");
      }
    } catch (err: any) {
      console.error("Failed to select group:", err);
      setError("Errore nella selezione del gruppo. Riprova.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="group-selection-page">
        <div className="group-selection-container">
          <div className="loading-message">
            <div className="loading-spinner"></div>
            <p>Caricamento gruppi...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && groups.length === 0) {
    return (
      <div className="group-selection-page">
        <div className="group-selection-container">
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group-selection-page">
      <div className="group-selection-container">
        <div className="group-selection-header">
          <h1 className="group-selection-title">
            Sembra tu faccia parte di più gruppi, a quale vuoi accedere?
          </h1>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <p>{error}</p>
          </div>
        )}

        <div
          className="groups-list"
          role="radiogroup"
          aria-label="Seleziona un gruppo"
        >
          {groups.map((group) => (
            <label
              key={group.id}
              className={`group-option ${
                selectedGroupSlug === group.slug ? "selected" : ""
              }`}
              aria-label={`Seleziona gruppo ${group.name}`}
            >
              <input
                type="radio"
                name="selectedGroup"
                value={group.slug}
                checked={selectedGroupSlug === group.slug}
                onChange={() => handleGroupSelect(group.slug)}
                className="group-radio-input"
              />
              <div className="group-info">
                <h3 className="group-name">
                  {group.name || `Gruppo ${group.slug}`}
                </h3>
                <div className="group-details">
                  <span className="member-count">
                    {group.memberCount} membri
                  </span>
                  {group.isAdmin && (
                    <span className="admin-badge">Amministratore</span>
                  )}
                </div>
              </div>
              <div className="group-selector">
                <div
                  className={`radio-button ${
                    selectedGroupSlug === group.slug ? "checked" : ""
                  }`}
                >
                  {selectedGroupSlug === group.slug && (
                    <div className="radio-dot"></div>
                  )}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div className="group-selection-actions">
          <button
            className="continue-button"
            onClick={handleSubmit}
            disabled={!selectedGroupSlug || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="loading-spinner"></div>
                <span>Accesso in corso...</span>
              </>
            ) : (
              "Continua"
            )}
          </button>

          {user && (
            <div className="logout-section">
              <p className="logged-in-text">
                Sei loggato come <strong>{user.email}</strong>
                <br />
                Vuoi cambiare account?{" "}
                <button
                  className="logout-link"
                  onClick={async () => {
                    try {
                      await logout();
                      navigate("/login");
                    } catch (error) {
                      console.error("Logout failed:", error);
                      // Fallback: navigate anyway
                      navigate("/login");
                    }
                  }}
                >
                  Esci
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupSelectionPage;
